import { useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "../../context/AuthContext";
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    LinearProgress,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import SnackbarAlert from "../../components/SnackBar";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import MovieCreationOutlinedIcon from "@mui/icons-material/MovieCreationOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { uploadCourseVideo } from "../../helper/uploadVideoChunks";
import { useConstants } from "../../hooks/UseConstants";
import { resolveCourseImage } from "../../helper/resolveMediaUrl";
import Fetch from "../../services/Fetch";
import PageContent from "../../components/PageContent";
import PageHero from "../../components/motion/PageHero";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import ScrollReveal from "../../components/motion/ScrollReveal";

function formatFileSize(bytes) {
    if (!bytes) return "0 B";
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileKind(file) {
    if (!file) return null;

    const mime = String(file.type || "").toLowerCase();
    const name = String(file.name || "").toLowerCase();

    if (mime === "application/pdf" || name.endsWith(".pdf")) return "pdf";
    if (
        mime.startsWith("video/")
        || [".mp4", ".mov", ".avi", ".webm", ".ogg", ".m3u8"].some((ext) => name.endsWith(ext))
    ) {
        return "video";
    }

    return "other";
}

function AddFile() {
    const { host, language } = useConstants();
    const isAr = language === "ar";
    const theme = useTheme();
    const { wait } = useContext(AuthContext);
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const { getWait, setGetWait, sendWait, setSendWait } = useWaits();
    const [course, setCourse] = useState(null);
    const [nameEn, setNameEn] = useState("");
    const [nameAr, setNameAr] = useState("");
    const [file, setFile] = useState(null);
    const [thumbnail, setThumbnail] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState("");
    const [uploadProgress, setUploadProgress] = useState(0);
    const navigate = useNavigate();
    const param = useParams();

    const courseName = useMemo(() => {
        if (!course) return "";
        return isAr ? course.name_ar : course.name_en;
    }, [course, isAr]);

    const courseImage = resolveCourseImage(course);
    const fileKind = useMemo(() => getFileKind(file), [file]);
    const isChunked = fileKind === "video" && file?.size > 8 * 1024 * 1024;

    const loadCourse = async () => {
        setGetWait(true);
        const result = await Fetch(`${host}/courses/${param.course_id}/show`, "GET", null);
        if (result.status === 200) {
            setCourse(result.data.data);
        }
        setGetWait(false);
    };

    useEffect(() => {
        loadCourse();
    }, [param.course_id]);

    useEffect(() => {
        if (!thumbnail) {
            setThumbnailPreview("");
            return undefined;
        }
        const url = URL.createObjectURL(thumbnail);
        setThumbnailPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [thumbnail]);

    const handleFileSelect = (selected) => {
        setFile(selected || null);
    };

    const addFile = async () => {
        if (!nameEn.trim() || !nameAr.trim()) {
            setSnackBar("error", isAr ? "أدخل اسم الدرس بالعربية والإنجليزية" : "Enter lesson name in Arabic and English");
            return;
        }
        if (!file) {
            setSnackBar("error", isAr ? "اختر ملف فيديو أو PDF" : "Select a video or PDF file");
            return;
        }
        if (fileKind === "other") {
            setSnackBar("error", isAr ? "يسمح فقط بملفات الفيديو أو PDF" : "Only video or PDF files are allowed");
            return;
        }

        setSendWait(true);
        setUploadProgress(0);

        try {
            await uploadCourseVideo({
                host,
                file,
                courseId: param.course_id,
                nameEn: nameEn.trim(),
                nameAr: nameAr.trim(),
                thumbnail: thumbnail || null,
                onProgress: setUploadProgress,
            });
            setSnackBar("success", isAr ? "تم رفع الملف وحفظه داخل الدورة" : "File uploaded and saved to the course");
            navigate(`/course-details/${param.course_id}`);
        } catch (err) {
            setSnackBar("error", err.message || (isAr ? "فشل الرفع" : "Upload failed"));
        }

        setSendWait(false);
    };

    const fieldSx = { width: "100%" };

    return (
        <>
            <PageContent wait={wait} loading={getWait}>
                <PageHero
                    title={isAr ? "إضافة ملف للدورة" : "Add course file"}
                    subtitle={isAr ? "ارفع فيديو أو ملف PDF واربطه بمحتوى الدورة" : "Upload a video or PDF lesson linked to your course"}
                    icon={<CloudUploadIcon />}
                    actions={
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate(`/course-details/${param.course_id}`)}
                            sx={{ color: "white", borderColor: "rgba(255,255,255,0.6)", "&:hover": { borderColor: "white" } }}
                        >
                            {isAr ? "عودة للدورة" : "Back to course"}
                        </Button>
                    }
                />

                {course ? (
                    <ScrollReveal>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mb: 3,
                                p: 2,
                                borderRadius: 3,
                                bgcolor: theme.palette.background.paper,
                                border: "1px solid",
                                borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                            }}
                        >
                            <Box
                                sx={{
                                    width: 72,
                                    height: 72,
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    flexShrink: 0,
                                    bgcolor: "#0D358C",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {courseImage ? (
                                    <Box component="img" src={courseImage} alt={courseName} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                ) : (
                                    <MenuBookOutlinedIcon sx={{ color: "white", fontSize: 36 }} />
                                )}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    {isAr ? "الدورة المختارة" : "Selected course"}
                                </Typography>
                                <Typography variant="h6" fontWeight={800} sx={{ color: "#0D358C" }} noWrap>
                                    {courseName}
                                </Typography>
                                <Chip size="small" label={`ID #${course.id}`} sx={{ mt: 0.5 }} />
                            </Box>
                        </Box>
                    </ScrollReveal>
                ) : null}

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3 }}>
                    <ScrollReveal delay={50}>
                        <DashboardPanelCard
                            title={isAr ? "معلومات الدرس" : "Lesson details"}
                            subtitle={isAr ? "يُحفظ الاسم في قاعدة البيانات مع ملف الجلسة" : "Names are saved to the database with the lesson file"}
                        >
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <TextField
                                    sx={fieldSx}
                                    value={nameEn}
                                    onChange={(e) => setNameEn(e.target.value)}
                                    label={isAr ? "اسم الدرس بالإنجليزية" : "Lesson name (English)"}
                                    variant="outlined"
                                    size="small"
                                    required
                                />
                                <TextField
                                    sx={fieldSx}
                                    value={nameAr}
                                    onChange={(e) => setNameAr(e.target.value)}
                                    label={isAr ? "اسم الدرس بالعربية" : "Lesson name (Arabic)"}
                                    variant="outlined"
                                    size="small"
                                    required
                                />
                            </Box>
                        </DashboardPanelCard>
                    </ScrollReveal>

                    <ScrollReveal delay={100}>
                        <DashboardPanelCard
                            title={isAr ? "صورة أو غلاف (اختياري)" : "Cover image (optional)"}
                            subtitle={isAr ? "تظهر للطلاب مع الجلسة في قائمة المحتوى" : "Shown to students with the lesson item"}
                        >
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                                <Box
                                    component="label"
                                    sx={{
                                        position: "relative",
                                        width: 160,
                                        height: 100,
                                        borderRadius: 2,
                                        border: "2px dashed",
                                        borderColor: thumbnailPreview ? "#0D358C" : "#94a3b8",
                                        overflow: "hidden",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        bgcolor: theme.palette.mode === "dark" ? "background.default" : "#f8fafc",
                                        "&:hover": { borderColor: "#0D358C" },
                                    }}
                                >
                                    {thumbnailPreview ? (
                                        <Box component="img" src={thumbnailPreview} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                    ) : (
                                        <AddPhotoAlternateIcon sx={{ fontSize: 40, color: "text.secondary" }} />
                                    )}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={(e) => setThumbnail(e.target.files?.[0] || null)}
                                    />
                                </Box>
                                <Typography variant="caption" sx={{ color: "text.secondary", textAlign: "center" }}>
                                    {isAr ? "انقر لاختيار صورة JPG أو PNG" : "Click to choose JPG or PNG image"}
                                </Typography>
                            </Box>
                        </DashboardPanelCard>
                    </ScrollReveal>
                </Box>

                <ScrollReveal delay={150} className="mt-3">
                    <DashboardPanelCard
                        title={isAr ? "ملف الجلسة" : "Lesson file"}
                        subtitle={isAr ? "يدعم فيديوهات MP4, MOV, AVI, WebM وملفات PDF" : "Supports MP4, MOV, AVI, WebM videos and PDF files"}
                    >
                        <Box
                            component="label"
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1.5,
                                p: 4,
                                borderRadius: 3,
                                border: "2px dashed",
                                borderColor: file ? "#2563eb" : "#cbd5e1",
                                bgcolor: file
                                    ? (theme.palette.mode === "dark" ? "rgba(37,99,235,0.12)" : "#eff6ff")
                                    : (theme.palette.mode === "dark" ? "background.default" : "#f8fafc"),
                                cursor: "pointer",
                                transition: "all 0.2s",
                                "&:hover": { borderColor: "#0D358C", bgcolor: theme.palette.mode === "dark" ? "rgba(13,53,140,0.15)" : "#eef2ff" },
                            }}
                        >
                            <CloudUploadIcon sx={{ fontSize: 48, color: file ? "#2563eb" : "#64748b" }} />
                            <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#0D358C" }}>
                                {file
                                    ? (isAr ? "تم اختيار الملف — انقر لتغييره" : "File selected — click to change")
                                    : (isAr ? "اسحب الملف هنا أو انقر للاختيار" : "Drag a file here or click to browse")}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                {fileKind === "pdf"
                                    ? (isAr ? "يتم رفع ملف PDF مباشرة إلى الدورة" : "PDF files are uploaded directly to the course")
                                    : (isAr ? "الحد الأقصى للرفع المباشر: 8 MB — وبعدها يتم رفع الفيديو على أجزاء تلقائياً" : "Direct upload up to 8 MB — larger videos use chunked upload")}
                            </Typography>
                            <input
                                type="file"
                                hidden
                                accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,.mp4,.mov,.avi,.webm,application/pdf,.pdf"
                                onChange={(e) => handleFileSelect(e.target.files?.[0])}
                            />
                        </Box>

                        {file ? (
                            <Box
                                sx={{
                                    mt: 2,
                                    p: 2,
                                    borderRadius: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    bgcolor: theme.palette.mode === "dark" ? "background.default" : "#f1f5f9",
                                }}
                            >
                                {fileKind === "pdf"
                                    ? <PictureAsPdfOutlinedIcon sx={{ color: "#0D358C", fontSize: 32 }} />
                                    : <MovieCreationOutlinedIcon sx={{ color: "#0D358C", fontSize: 32 }} />}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={700} noWrap>
                                        {file.name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                        {formatFileSize(file.size)}
                                        {fileKind === "pdf"
                                            ? (isAr ? " • ملف PDF" : " • PDF document")
                                            : isChunked
                                                ? (isAr ? " • رفع فيديو على أجزاء" : " • chunked video upload")
                                                : (isAr ? " • رفع مباشر" : " • direct upload")}
                                    </Typography>
                                </Box>
                            </Box>
                        ) : null}

                        {sendWait && uploadProgress > 0 ? (
                            <Box sx={{ mt: 3 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                        {isAr ? "جاري الرفع والحفظ..." : "Uploading and saving..."}
                                    </Typography>
                                    <Typography variant="caption" fontWeight={700}>
                                        {uploadProgress}%
                                    </Typography>
                                </Box>
                                <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 8, borderRadius: 4 }} />
                            </Box>
                        ) : null}

                        <Box sx={{ mt: 3, display: "flex", flexWrap: "wrap", gap: 1.5, justifyContent: "flex-end" }}>
                            <Button
                                variant="outlined"
                                onClick={() => navigate(`/course-details/${param.course_id}`)}
                                disabled={sendWait}
                            >
                                {isAr ? "إلغاء" : "Cancel"}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={addFile}
                                disabled={sendWait || !file}
                                startIcon={sendWait ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                                sx={{ minWidth: 160, bgcolor: "#0D358C", "&:hover": { bgcolor: "#1d4ed8" } }}
                            >
                                {sendWait ? (isAr ? "جاري الرفع..." : "Uploading...") : (isAr ? "رفع وحفظ" : "Upload & save")}
                            </Button>
                        </Box>
                    </DashboardPanelCard>
                </ScrollReveal>
            </PageContent>
            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </>
    );
}

export default AddFile;
